// src/components/ZuSessionProvider.tsx

"use client";

import React, { useEffect, useCallback, useState } from "react";
import { useSession, signOut } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { useSessionStore } from "@/stores/sessionStore";
import debounce from "lodash/debounce";

interface ZuSessionProviderProps {
  children: React.ReactNode;
}

export const ZuSessionProvider: React.FC<ZuSessionProviderProps> = ({ children }) => {
  const { data: session, status, update } = useSession();
  const { setSession } = useSessionStore();
  const router = useRouter();
  const pathname = usePathname();
  const [authReady, setAuthReady] = useState(false);

  // 1) Check expiry + reach out to /api/auth/session to confirm user
  const checkSessionStatus = useCallback(
    debounce(async () => {
      if (!session?.expires || status !== "authenticated") return;

      const expiresAt = new Date(session.expires).getTime();
      const now = Date.now();
      if (now >= expiresAt) {
        console.log("Session expired, signing out...");
        signOut({ callbackUrl: "/auth", redirect: true });
        return;
      }

      // Verify with our session endpoint
      try {
        const res = await fetch("/api/auth/session", {
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
          console.error(`Session fetch failed: ${res.status}`);
          signOut({ callbackUrl: "/auth", redirect: true });
          return;
        }
        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          console.error("Non-JSON session response:", ct);
          signOut({ callbackUrl: "/auth", redirect: true });
          return;
        }
        const data = await res.json();
        if (!data.user) {
          console.log("No user in session data, signing out...");
          signOut({ callbackUrl: "/auth", redirect: true });
        }
      } catch (err) {
        console.error("Failed session check:", err);
        signOut({ callbackUrl: "/auth", redirect: true });
      }
    }, 1000, { leading: true, trailing: false }),
    [session, status]
  );

  // Session Validation for Protected Routes
  useEffect(() => {
    const protectedRoutes = ["/dashboard", "/admin"];
    const isProtected = protectedRoutes.some((r) => pathname.startsWith(r));
    const isAuthRoute = pathname.startsWith("/auth");

    // 1. On auth pages, we clear any old session and let them sign in
    if (isAuthRoute) {
      setSession(null);
      setAuthReady(true);
      return;
    }

    // 2. If unauthenticated on protected, sign out
    if (status === "unauthenticated" && isProtected) {
      console.log("Unauthenticated on protected route, redirecting to /auth");
      setSession(null);
      signOut({ callbackUrl: "/auth", redirect: true });
      return;
    }

    // 3. Only if session.user is *truly undefined* do we sign out
    if (status === "authenticated" && session?.user === undefined && isProtected) {
      console.log("Authenticated but no session.user, signing out...");
      setSession(null);
      signOut({ callbackUrl: "/auth", redirect: true });
      return;
    }

    // 4. Otherwise session is valid — store it and mark ready
    setSession(session ?? null);
    setAuthReady(true);

    // 5. Kick off our expiry + verification checks
    if (authReady && status === "authenticated" && session?.expires && isProtected) {
      checkSessionStatus();
      const iv = setInterval(checkSessionStatus, 10_000);
      return () => {
        clearInterval(iv);
        checkSessionStatus.cancel();
      };
    }
  }, [session, status, pathname, authReady, checkSessionStatus, setSession]);

  // Token Refresh: every 15 min, call /api/auth/session and update
  useEffect(() => {
    if (
      authReady &&
      status === "authenticated" &&
      session?.user &&
      !pathname.startsWith("/auth")
    ) {
      const iv = setInterval(async () => {
        try {
          const res = await fetch("/api/auth/session", {
            headers: { "Content-Type": "application/json" },
          });
          if (!res.ok) throw new Error("Failed to fetch session");
          const data = await res.json();
          if (!data.user?.token) throw new Error("No token in session");
          console.log("Session token refreshed:", data.user.token.slice(0, 10), "...");
          await update({ token: data.user.token });
        } catch (err) {
          console.error("Token refresh failed:", err);
          signOut({ callbackUrl: "/auth", redirect: true });
        }
      }, 15 * 60 * 1000);

      return () => clearInterval(iv);
    }
  }, [authReady, status, session, pathname, update]);

  // While we’re loading or not yet ready, render nothing
  if (status === "loading" || !authReady) {
    return null;
  }

  return <>{children}</>;
};
